/**
 * @fileoverview Welcome status utilities for cross-device welcome completion check
 * @module utils/welcomeStatus
 *
 * Checks whether a user has completed the welcome wizard, supporting
 * cross-device sync via Firestore while caching locally for performance.
 *
 * Data location: users_global/{userId}/memberships/{tenantId}.welcomeCompleted
 */
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/plugins/firebase'

const WELCOME_COMPLETED_KEY = 'welcomeCompleted'

/**
 * Check if user has completed the welcome wizard
 *
 * Fast path: check localStorage first (browser-local cache)
 * Fallback: fetch from Firestore and cache the result
 *
 * @param userId - The authenticated user's UID
 * @param tenantId - The user's current tenant ID
 * @returns true if welcome was completed, false otherwise
 */
export async function checkWelcomeCompleted(
  userId: string | null,
  tenantId: string | null
): Promise<boolean> {
  // If no user or tenant, they haven't completed welcome
  if (!userId || !tenantId) {
    return false
  }

  // Fast path: check localStorage cache
  const cachedValue = localStorage.getItem(WELCOME_COMPLETED_KEY)
  if (cachedValue === 'true') {
    return true
  }

  // Fallback: fetch from Firestore
  try {
    const membershipRef = doc(db, `users_global/${userId}/memberships/${tenantId}`)
    const membershipSnap = await getDoc(membershipRef)

    if (membershipSnap.exists()) {
      const data = membershipSnap.data()
      const welcomeCompleted = data?.welcomeCompleted === true

      // Cache the result in localStorage for future fast-path lookups
      if (welcomeCompleted) {
        localStorage.setItem(WELCOME_COMPLETED_KEY, 'true')
      }

      return welcomeCompleted
    }
  } catch (error) {
    // Log error but don't throw - fail open to avoid blocking users
    console.error('Failed to fetch welcome status from Firestore:', error)
  }

  return false
}

/**
 * Mark welcome as completed in localStorage
 * Called by the welcome wizard when user completes it
 *
 * Note: Firestore update should be handled separately by the wizard
 */
export function markWelcomeCompleted(): void {
  localStorage.setItem(WELCOME_COMPLETED_KEY, 'true')
}

/**
 * Clear welcome status cache from localStorage
 * Should be called on sign-out to reset state
 */
export function clearWelcomeStatusCache(): void {
  localStorage.removeItem(WELCOME_COMPLETED_KEY)
}
